import { useEffect, useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useAtom, useSetAtom } from 'jotai'
import { Edit, Trash2, RefreshCw } from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  chatThreadsAtom,
  deleteThreadAtom,
  isDisplayableWorkspaceChatThread,
  loadThreadsAtom,
  updateThreadNameAtom,
} from '@/components/chat/store/chatThreadStore'
import { ChatThreadMetaData } from '../ChatThreadManager'

export function ChatThreadList({
  onSelect,
  workspaceId,
}: {
  onSelect?: (id: string) => void
  workspaceId?: string | null
}) {
  const [threads] = useAtom(chatThreadsAtom)
  const loadThreads = useSetAtom(loadThreadsAtom)
  const deleteThread = useSetAtom(deleteThreadAtom)
  const updateThreadName = useSetAtom(updateThreadNameAtom)
  const [newThreadName, setNewThreadName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [archivedFilter, setArchivedFilter] = useState<'active' | 'archived' | 'all'>('active')
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Bug Fix: Manual refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await loadThreads()
    } finally {
      setIsRefreshing(false)
    }
  }

  // Bug Fix: Periodic auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadThreads()
    }, 30000)

    return () => clearInterval(interval)
  }, [loadThreads])

  // Initial load
  useEffect(() => {
    const loadThreadsData = async () => {
      setIsLoading(true)
      try {
        await loadThreads()
      } catch (error) {
        console.error('Failed to load threads:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadThreadsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const handleThreadSelect = (threadId: string) => {
    onSelect?.(threadId)
  }

  const handleEditClick = (e: React.MouseEvent, thread: ChatThreadMetaData) => {
    e.stopPropagation()
    setNewThreadName(thread.name)
  }

  const handleNameSubmit = async (e: React.FormEvent, id: string) => {
    e.preventDefault()

    if (id && newThreadName.trim()) {
      await updateThreadName({
        threadId: id,
        name: newThreadName.trim(),
      })
    }
  }

  const filteredThreads = useMemo(() => {
    const byArchive = threads.filter((thread) => {
      if (!isDisplayableWorkspaceChatThread(thread, workspaceId)) return false
      if (archivedFilter === 'active') return !thread.archivedAt
      if (archivedFilter === 'archived') return !!thread.archivedAt
      return true
    })
    if (!searchQuery) return byArchive
    return byArchive.filter((thread) =>
      thread.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [threads, searchQuery, archivedFilter, workspaceId])

  const filterItemsByTime = (
    items: ChatThreadMetaData[],
    timeFilter: string
  ) => {
    const now = new Date()
    const oneDay = 24 * 60 * 60 * 1000
    const oneWeek = 7 * oneDay
    const oneMonth = 30 * oneDay

    const filteredItems = items.filter((item) => {
      const itemDate = new Date(item.updatedAt)
      const diff = now.getTime() - itemDate.getTime()

      switch (timeFilter) {
        case 'Today':
          return diff < oneDay
        case 'Yesterday':
          return diff >= oneDay && diff < 2 * oneDay
        case '2 days ago':
          return diff >= 2 * oneDay && diff < 3 * oneDay
        case '3 days ago':
          return diff >= 3 * oneDay && diff < 4 * oneDay
        case '4 days ago':
          return diff >= 4 * oneDay && diff < 5 * oneDay
        case '5 days ago':
          return diff >= 5 * oneDay && diff < 6 * oneDay
        case '6 days ago':
          return diff >= 6 * oneDay && diff < 7 * oneDay
        case '1 week ago':
          return diff >= oneWeek && diff < 2 * oneWeek
        case '2 weeks ago':
          return diff >= 2 * oneWeek && diff < 3 * oneWeek
        case '3 weeks ago':
          return diff >= 3 * oneWeek && diff < oneMonth
        case '1 month ago':
          return diff >= oneMonth
        default:
          return false
      }
    })

    if (filteredItems.length === 0) return null

    const sortedItems = filteredItems.sort((a, b) => {
      const dateA = new Date(a.updatedAt)
      const dateB = new Date(b.updatedAt)
      return dateB.getTime() - dateA.getTime()
    })

    return (
      <div className='w-full overflow-hidden'>
        <div className='text-muted-foreground p-2 pb-1 text-xs leading-4'>
          {timeFilter}
        </div>
        <div className='flex w-full flex-col gap-0'>
          {sortedItems?.map((item) => (
            <ThreadItem
              key={item.id}
              item={item}
              onSelect={handleThreadSelect}
              onDelete={deleteThread}
              onEdit={handleEditClick}
              onNameChange={(name) => setNewThreadName(name)}
              onNameSubmit={handleNameSubmit}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='flex h-[calc(var(--app-vh)_-_45px)] w-full flex-col gap-2 pt-2 pb-4'>
      <div className='flex gap-1 px-4'>
        {(['active', 'archived', 'all'] as const).map((key) => {
          const label = key === 'active' ? 'Active' : key === 'archived' ? 'Archived' : 'All'
          const isActive = archivedFilter === key
          return (
            <button
              key={key}
              type='button'
              onClick={() => setArchivedFilter(key)}
              className={cn(
                'rounded-sm px-2 py-1 text-[12px] font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-text-subtle hover:bg-muted hover:text-text-secondary'
              )}
            >
              {label}
            </button>
          )
        })}
      </div>
      <div className='px-4 flex gap-2'>
        <Input
          type='text'
          placeholder='Search for past chats...'
          className='flex-1 border-border h-9 rounded-md py-2 pl-3 text-sm shadow-none'
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
          }}
        />
        {/* Bug Fix: Refresh button */}
        <Button
          variant='ghost'
          size='sm'
          onClick={handleRefresh}
          disabled={isRefreshing}
          className='h-9 w-9 p-0'
          title='Refresh threads'
        >
          <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
        </Button>
      </div>

      {isLoading ? (
        <div className='flex flex-1 items-center justify-center'>
          <div className='text-muted-foreground text-sm'>
            Loading threads...
          </div>
        </div>
      ) : (
        <ScrollArea
          className='h-[calc(100%-44px)] w-full px-4'
          viewClassName='[&>div]:!block'
        >
          <div className='flex w-full flex-col gap-0'>
            {filteredThreads.length === 0 ? (
              <div className='px-2 py-8 text-center text-[13px] text-text-subtle'>
                No conversations yet
              </div>
            ) : (
              <>
                {filterItemsByTime(filteredThreads, 'Today')}
                {filterItemsByTime(filteredThreads, 'Yesterday')}
                {filterItemsByTime(filteredThreads, '2 days ago')}
                {filterItemsByTime(filteredThreads, '3 days ago')}
                {filterItemsByTime(filteredThreads, '4 days ago')}
                {filterItemsByTime(filteredThreads, '5 days ago')}
                {filterItemsByTime(filteredThreads, '6 days ago')}
                {filterItemsByTime(filteredThreads, '1 week ago')}
                {filterItemsByTime(filteredThreads, '2 weeks ago')}
                {filterItemsByTime(filteredThreads, '3 weeks ago')}
                {filterItemsByTime(filteredThreads, '1 month ago')}
              </>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  )
}

// Helper function to render thread items
function ThreadItem({
  item,
  onSelect,
  onDelete,
  onNameChange,
  onNameSubmit,
}: {
  item: ChatThreadMetaData
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (e: React.MouseEvent, thread: ChatThreadMetaData) => void
  onNameChange: (name: string) => void
  onNameSubmit: (e: React.FormEvent, id: string) => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState(item.name)
  return (
    <>
      {isEditing ? (
        <div className='p-1'>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              onNameChange(e.target.value)
            }}
            onBlur={async (e) => {
              await onNameSubmit(e, item.id)
              setIsEditing(false)
            }}
            className='h-6'
          />
        </div>
      ) : (
        <div
          className='group hover:bg-accent flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5'
          onMouseDown={() => {
            setTimeout(() => {
              onSelect(item.id)
            }, 0)
          }}
        >
          <span className='text-popover-foreground flex w-full min-w-0 items-center gap-1.5 truncate text-[13px] leading-5 font-medium group-hover:w-[calc(100%-30px)]'>
            <span className='truncate'>{item.name?.trim() || 'New chat'}</span>
            {item.archivedAt && (
              <span className='shrink-0 rounded-sm bg-muted px-1 py-px text-[9px] font-medium uppercase tracking-[0.4px] text-text-subtle'>
                Archived
              </span>
            )}
          </span>
          <span className='text-muted-foreground w-50 truncate text-right text-xs opacity-100 group-hover:opacity-0'>
            {formatDistanceToNow(new Date(item.updatedAt), {
              addSuffix: true,
            })}
          </span>
          <Button
            variant='ghost'
            className='text-muted-foreground box-border hidden !size-5 rounded-sm !p-0.5 group-hover:block'
            onMouseDown={(e) => {
              e.stopPropagation()
              setIsEditing(true)
            }}
          >
            <Edit size={16} />
          </Button>

          <Button
            variant='ghost'
            className='text-muted-foreground box-border hidden !size-5 rounded-md !p-0.5 group-hover:block'
            onMouseDown={(e) => {
              e.stopPropagation()
              onDelete(item.id)
            }}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      )}
    </>
  )
}
